import React, { useEffect, useState } from 'react';
import { X, Sparkles, Loader2, Wand2 } from 'lucide-react';

interface AITextEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (replacementText: string) => void;
  fieldLabel: string;
  selectedText: string;
  hasSelection: boolean;
  statisticTitle?: string;
  fullText: string;
  selectionStart: number;
  selectionEnd: number;
  articleField: string;
}

const AITextEditModal: React.FC<AITextEditModalProps> = ({
  isOpen,
  onClose,
  onApply,
  fieldLabel,
  selectedText,
  hasSelection,
  statisticTitle,
  fullText,
  selectionStart,
  selectionEnd,
  articleField,
}) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPrompt('');
      setError(null);
      setPreview(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);
    setPreview(null);

    try {
      const response = await fetch('/api/markdown/ai-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fullText,
          field: articleField,
          selectionStart,
          selectionEnd,
          userPrompt: prompt.trim(),
          statisticTitle,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al generar texto con IA');
      }

      setPreview(data.replacementText);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al generar texto con IA');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (preview !== null) {
      onApply(preview);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
        <div className="relative px-6 py-5 border-b border-border bg-gradient-to-r from-violet-600/10 via-purple-600/10 to-fuchsia-600/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(139,92,246,0.15),transparent_50%)]" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/25">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Asistente IA de texto</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {fieldLabel} · {hasSelection ? 'Editar selección' : 'Insertar en cursor'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="rounded-lg border border-violet-200/60 bg-violet-50/50 dark:bg-violet-950/20 dark:border-violet-800/40 p-3">
            <p className="text-xs font-medium text-violet-700 dark:text-violet-300 mb-1">
              {hasSelection ? 'Texto seleccionado' : 'Posición del cursor'}
            </p>
            <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-4 font-mono bg-background/60 rounded p-2 border border-border/50">
              {hasSelection ? selectedText : '(Se insertará texto nuevo en la posición actual del cursor)'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              ¿Qué quieres que haga la IA?
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                hasSelection
                  ? 'Ej: Amplía este párrafo con más contexto, datos concretos y matices; mantén el tono Statemia...'
                  : 'Ej: Escribe 3-4 párrafos que contextualicen la estadística, con narrativa periodística y datos del artículo...'
              }
              rows={4}
              className="w-full p-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-none"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}

          {preview !== null && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800/40 p-3">
              <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-2">Vista previa del resultado</p>
              <div className="text-sm text-foreground whitespace-pre-wrap max-h-48 overflow-y-auto font-mono bg-background/60 rounded p-3 border border-border/50">
                {preview}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-surface-50">
          <button onClick={onClose} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerate}
              disabled={loading || !prompt.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md shadow-violet-500/20"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              {loading ? 'Generando...' : preview ? 'Regenerar' : 'Generar'}
            </button>
            {preview !== null && (
              <button onClick={handleApply} className="btn-primary flex items-center gap-2">
                <Sparkles size={16} />
                Aplicar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITextEditModal;
